import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useScreenState } from '@/hooks/useScreenState';
import { ScreenType } from '@/types/screen.types';
import { Action } from '@/business/types/action.types';
import { useThemeColors, withOpacity } from '@/styles';
import { spacing } from '@/styles/design-tokens';
import { JobType, JobRunStatus } from '@/types/enums';
import { ComponentHealthStatus } from '@/business/types/ComponentHealthStatus';
import { BoardProvisionAction } from '@/business/types/BoardProvisionAction';
import { ShareButton } from '@/components/core/ShareButton/ShareButton';
import { DeepLinkEntity } from '@/utils/deep-linking';
import { formatTimestamp } from '@/utils/date';
import { executeQuery, QueryError } from '@/lib/execute-query';
import { ResponseCode } from '@/business/types/response-codes.types';
import type {
  GetJobRunInput,
  GetJobRunOutput,
  GetJobRunsRunsItem,
} from '@/types/generated';

// ─── Constants ───────────────────────────────────────────────────────────────

const JOB_TYPE_LABELS: Record<string, string> = {
  [JobType.WEEKLY_CARRY_OVER]: 'Weekly Carry-Over',
  [JobType.SYNC_BOARD_TEMPLATES]: 'Sync Board Templates',
  [JobType.SYSTEM_HEALTH_CHECK]: 'System Health Check',
  [JobType.ENSURE_USER_BOARDS]: 'Ensure User Boards',
};

const RUN_STATUS_DISPLAY: Record<JobRunStatus, string> = {
  [JobRunStatus.RUNNING]: 'Running',
  [JobRunStatus.COMPLETED]: 'Completed',
  [JobRunStatus.FAILED]: 'Failed',
  [JobRunStatus.PARTIAL]: 'Partial',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  return formatTimestamp(iso, 'datetime') || iso;
}

function formatDuration(startedAt: string, completedAt?: string): string {
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - start;
  if (ms <= 0) return '< 1ms';
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return remSec > 0 ? `${min}m ${remSec}s` : `${min}m`;
}

function readOutput(summary: Record<string, unknown>, field: string): unknown {
  const output = summary.output as Record<string, unknown> | undefined;
  if (output && field in output) return output[field];
  return summary[field];
}

function readInput(summary: Record<string, unknown>, field: string): unknown {
  const input = summary.input as Record<string, unknown> | undefined;
  if (input && field in input) return input[field];
  return summary[field];
}

// ─── Type-Specific Detail Components ─────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  assignee: 'Assignee',
  reviewer: 'Reviewer',
  observer: 'Observer',
  unassigned: 'Unassigned',
};

interface Assignment {
  user_id: string;
  display_name: string;
  relation: string;
}

interface TaskEntry {
  source_task_id: string;
  target_task_id?: string;
  title: string;
  board_title: string;
  deeplink?: string;
  skipped?: boolean;
  assignments?: Assignment[];
}

// ─── New grouped summary types (backend v2 format) ───────────────────────────

interface BoardEntry {
  board_id: string;
  board_title: string;
  tasks: TaskEntry[];
}

interface UserEntry {
  user_id: string;
  display_name: string;
  total_tasks: number;
  boards: BoardEntry[];
}

interface RoleGroup {
  role: string;
  total_tasks: number;
  users: UserEntry[];
}

// buildCarryOverTree supports legacy flat-cards format (backend v1).
function buildCarryOverTree(tasks: TaskEntry[]): RoleGroup[] {
  const roleMap = new Map<string, Map<string, TaskEntry[]>>();

  for (const task of tasks) {
    if (task.skipped) continue;
    const assignments = task.assignments ?? [];
    if (assignments.length === 0) {
      const role = 'unassigned';
      if (!roleMap.has(role)) roleMap.set(role, new Map());
      const userMap = roleMap.get(role)!;
      const key = '__unassigned__';
      if (!userMap.has(key)) userMap.set(key, []);
      userMap.get(key)!.push(task);
    } else {
      for (const a of assignments) {
        const role = a.relation || 'assignee';
        if (!roleMap.has(role)) roleMap.set(role, new Map());
        const userMap = roleMap.get(role)!;
        if (!userMap.has(a.user_id)) userMap.set(a.user_id, []);
        const existing = userMap.get(a.user_id)!;
        if (!existing.some(c => c.source_task_id === task.source_task_id)) {
          existing.push({ ...task, assignments: [a] });
        }
      }
    }
  }

  const groups: RoleGroup[] = [];
  for (const [role, userMap] of roleMap) {
    const users: UserEntry[] = [];
    let totalTasks = 0;
    for (const [userId, userCards] of userMap) {
      const displayName =
        userId === '__unassigned__'
          ? 'Unassigned'
          : (userCards[0]?.assignments?.[0]?.display_name ?? userId);
      // Flatten into a single board entry for legacy format
      users.push({
        user_id: userId,
        display_name: displayName,
        total_tasks: userCards.length,
        boards: [{ board_id: '', board_title: '', tasks: userCards }],
      });
      totalTasks += userCards.length;
    }
    users.sort((a, b) => {
      if (b.total_tasks !== a.total_tasks) return b.total_tasks - a.total_tasks;
      return a.display_name.localeCompare(b.display_name);
    });
    groups.push({ role, total_tasks: totalTasks, users });
  }

  groups.sort((a, b) => {
    if (b.total_tasks !== a.total_tasks) return b.total_tasks - a.total_tasks;
    return a.role.localeCompare(b.role);
  });

  return groups;
}

function CarryOverDetail({ summary }: { summary: Record<string, unknown> }) {
  const colors = useThemeColors();
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const sourceWeek = readInput(summary, 'source_week');
  const sourceYear = readInput(summary, 'source_year');
  const targetWeek = readInput(summary, 'target_week');
  const targetYear = readInput(summary, 'target_year');
  const weekStart = readInput(summary, 'week_start') as string | undefined;
  const targetStart = readInput(summary, 'target_start') as string | undefined;

  // Support both new grouped format (roles) and legacy flat format (cards)
  const rolesRaw = readOutput(summary, 'roles') as RoleGroup[] | undefined;
  const tasksRaw = (readOutput(summary, 'tasks') ?? []) as TaskEntry[];
  const tree = useMemo(
    () => rolesRaw ?? buildCarryOverTree(tasksRaw),
    [rolesRaw, tasksRaw]
  );
  const skippedCount = tasksRaw.filter(c => c.skipped).length;

  const toggleRole = (role: string) => {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      next.has(role) ? next.delete(role) : next.add(role);
      return next;
    });
  };

  const toggleUser = (key: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <div className="p-4 flex flex-col gap-3">
      {sourceWeek != null && (
        <div className="flex items-center gap-3 text-xs">
          <span
            className="px-2 py-1 rounded font-mono"
            style={{
              backgroundColor: withOpacity(colors.primary, 0.08),
              color: colors.primary,
            }}
          >
            Source: W{String(sourceWeek)}, {String(sourceYear)}
            {weekStart ? ` (${weekStart})` : ''}
          </span>
          <span style={{ color: colors.text.secondary }}>→</span>
          <span
            className="px-2 py-1 rounded font-mono"
            style={{
              backgroundColor: withOpacity(colors.success, 0.08),
              color: colors.success,
            }}
          >
            Target: W{String(targetWeek)}, {String(targetYear)}
            {targetStart ? ` (${targetStart})` : ''}
          </span>
        </div>
      )}

      {skippedCount > 0 && (
        <div
          className="text-xs italic"
          style={{ color: colors.text.secondary }}
        >
          {skippedCount} card{skippedCount !== 1 ? 's' : ''} skipped (already
          carried over)
        </div>
      )}

      {tree.length === 0 ? (
        <div className="text-xs" style={{ color: colors.text.secondary }}>
          No cards carried over.
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {tree.map(group => (
            <div key={group.role}>
              <button
                className="flex items-center gap-2 w-full text-left text-xs font-medium py-1.5 px-3 rounded hover:opacity-80"
                style={{
                  backgroundColor: withOpacity(colors.text.secondary, 0.06),
                }}
                onClick={() => toggleRole(group.role)}
              >
                <span className="text-[10px]">
                  {expandedRoles.has(group.role) ? '▼' : '▶'}
                </span>
                <span>{ROLE_LABELS[group.role] ?? group.role}</span>
                <span
                  className="tabular-nums ml-auto"
                  style={{ color: colors.text.secondary }}
                >
                  {group.total_tasks} task{group.total_tasks !== 1 ? 's' : ''}
                </span>
              </button>

              {expandedRoles.has(group.role) && (
                <div className="ml-5 flex flex-col gap-0.5 mt-0.5">
                  {group.users.map(user => {
                    const userKey = `${group.role}:${user.user_id}`;
                    return (
                      <div key={userKey}>
                        <button
                          className="flex items-center gap-2 w-full text-left text-xs py-1.5 px-3 rounded hover:opacity-80"
                          style={{
                            backgroundColor: withOpacity(
                              colors.text.secondary,
                              0.03
                            ),
                          }}
                          onClick={() => toggleUser(userKey)}
                        >
                          <span className="text-[10px]">
                            {expandedUsers.has(userKey) ? '▼' : '▶'}
                          </span>
                          <span className="font-medium">
                            {user.display_name}
                          </span>
                          <span
                            className="tabular-nums ml-auto"
                            style={{ color: colors.text.secondary }}
                          >
                            {user.total_tasks}
                          </span>
                        </button>

                        {expandedUsers.has(userKey) && (
                          <div className="ml-7 flex flex-col gap-0.5 mt-0.5">
                            {user.boards.map(board =>
                              board.tasks.map(task => (
                                <a
                                  key={task.source_task_id}
                                  href={
                                    task.deeplink ??
                                    (task.target_task_id
                                      ? `/goto/task/${task.target_task_id}`
                                      : undefined)
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-xs py-1 px-3 rounded hover:opacity-80"
                                  style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    cursor:
                                      task.deeplink || task.target_task_id
                                        ? 'pointer'
                                        : 'default',
                                  }}
                                  onClick={e => {
                                    if (
                                      !task.deeplink &&
                                      !task.target_task_id
                                    ) {
                                      e.preventDefault();
                                    }
                                    e.stopPropagation();
                                  }}
                                >
                                  <span
                                    className="w-1.5 h-1.5 rounded-full shrink-0"
                                    style={{
                                      backgroundColor: colors.text.secondary,
                                    }}
                                  />
                                  <span className="truncate flex-1">
                                    {task.title}
                                  </span>
                                  {(board.board_title || task.board_title) && (
                                    <span
                                      className="text-[10px] shrink-0 truncate max-w-[160px]"
                                      style={{ color: colors.text.secondary }}
                                      title={
                                        board.board_title || task.board_title
                                      }
                                    >
                                      {board.board_title || task.board_title}
                                    </span>
                                  )}
                                </a>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface BoardDetail {
  board_id: string;
  board_title: string;
  template_name: string;
  statuses_added: number;
  statuses_updated: number;
  statuses_archived: number;
  error?: string;
}

function TemplateSyncDetail({ summary }: { summary: Record<string, unknown> }) {
  const colors = useThemeColors();
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(
    new Set()
  );

  const boards = (readOutput(summary, 'boards') ?? []) as BoardDetail[];

  const templateGroups = useMemo(() => {
    const map = new Map<string, BoardDetail[]>();
    for (const b of boards) {
      const key = b.template_name || 'Unknown Template';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(b);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [boards]);

  const toggleTemplate = (name: string) => {
    setExpandedTemplates(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  if (boards.length === 0) {
    return (
      <div className="p-4 text-xs" style={{ color: colors.text.secondary }}>
        No boards needed syncing.
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-1">
      {templateGroups.map(([templateName, templateBoards]) => (
        <div key={templateName}>
          <button
            className="flex items-center gap-2 w-full text-left text-xs font-medium py-1.5 px-3 rounded hover:opacity-80"
            style={{
              backgroundColor: withOpacity(colors.text.secondary, 0.06),
            }}
            onClick={() => toggleTemplate(templateName)}
          >
            <span className="text-[10px]">
              {expandedTemplates.has(templateName) ? '▼' : '▶'}
            </span>
            <span>{templateName}</span>
            <span
              className="tabular-nums ml-auto"
              style={{ color: colors.text.secondary }}
            >
              {templateBoards.length} board
              {templateBoards.length !== 1 ? 's' : ''}
            </span>
          </button>

          {expandedTemplates.has(templateName) && (
            <div className="ml-5 flex flex-col gap-0.5 mt-0.5">
              {templateBoards.map(board => {
                const changes: string[] = [];
                if (board.statuses_added)
                  changes.push(`+${board.statuses_added} status`);
                if (board.statuses_updated)
                  changes.push(`~${board.statuses_updated} status`);
                if (board.statuses_archived)
                  changes.push(`-${board.statuses_archived} status`);
                const totalChanges =
                  board.statuses_added +
                  board.statuses_updated +
                  board.statuses_archived;

                return (
                  <div
                    key={board.board_id}
                    className="flex items-center gap-2 text-xs py-1 px-3"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{
                        backgroundColor: board.error
                          ? colors.error
                          : colors.success,
                      }}
                    />
                    <span className="font-medium truncate">
                      {board.board_title || board.board_id.slice(0, 8)}
                    </span>
                    <span
                      className="text-[10px] ml-auto shrink-0"
                      style={{
                        color: board.error
                          ? colors.error
                          : colors.text.secondary,
                      }}
                    >
                      {board.error
                        ? 'failed'
                        : totalChanges === 0
                          ? 'no changes'
                          : changes.join(', ')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── EnsureUserBoards detail ─────────────────────────────────────────────────

interface BoardProvisionDetail {
  user_id: string;
  display_name: string;
  template_id: string;
  template_name: string;
  tag: string;
  board_id?: string;
  action: BoardProvisionAction;
  error?: string;
}

function EnsureUserBoardsDetail({
  summary,
}: {
  summary: Record<string, unknown>;
}) {
  const colors = useThemeColors();

  const details = (readOutput(summary, 'details') ??
    []) as BoardProvisionDetail[];

  const created = details.filter(
    d => d.action === BoardProvisionAction.CREATED
  );
  const skipped = details.filter(
    d => d.action === BoardProvisionAction.SKIPPED
  );
  const failed = details.filter(d => d.action === BoardProvisionAction.FAILED);

  if (details.length === 0) {
    return (
      <div className="p-4 text-xs" style={{ color: colors.text.secondary }}>
        No users processed.
      </div>
    );
  }

  const Section = ({
    title,
    items,
    dot,
  }: {
    title: string;
    items: BoardProvisionDetail[];
    dot: string;
  }) => {
    if (items.length === 0) return null;
    return (
      <div className="flex flex-col gap-0.5">
        <span
          className="text-[10px] uppercase tracking-wide px-3 mb-1"
          style={{ color: colors.text.secondary }}
        >
          {title}
        </span>
        {items.map((d, i) => (
          <div
            key={`${d.user_id}-${d.template_id}-${i}`}
            className="flex items-center gap-2 text-xs py-1.5 px-3 rounded"
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: dot }}
            />
            <span className="font-medium">{d.display_name}</span>
            <span
              className="text-[10px] shrink-0"
              style={{ color: colors.text.secondary }}
            >
              {d.template_name}
            </span>
            {d.error && (
              <span
                className="text-[10px] ml-auto shrink-0 truncate max-w-[200px]"
                style={{ color: colors.error }}
                title={d.error}
              >
                {d.error}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <Section title="Boards created" items={created} dot={colors.success} />
      <Section
        title="Already existed"
        items={skipped}
        dot={colors.text.secondary}
      />
      <Section title="Failed" items={failed} dot={colors.error} />
    </div>
  );
}

interface ComponentSummary {
  component: string;
  status: ComponentHealthStatus;
  metrics?: Record<string, unknown>;
}

function HealthCheckDetail({ summary }: { summary: Record<string, unknown> }) {
  const colors = useThemeColors();
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(
    new Set()
  );

  const components = (readOutput(summary, 'components') ??
    []) as ComponentSummary[];

  const statusColors: Record<ComponentHealthStatus, string> = {
    [ComponentHealthStatus.HEALTHY]: colors.success,
    [ComponentHealthStatus.DEGRADED]: colors.warning.text,
    [ComponentHealthStatus.UNHEALTHY]: colors.error,
    [ComponentHealthStatus.NOT_MONITORED]: colors.text.secondary,
    [ComponentHealthStatus.UNKNOWN]: colors.text.secondary,
  };

  const toggleComponent = (name: string) => {
    setExpandedComponents(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  if (components.length === 0) {
    return (
      <div className="p-4 text-xs" style={{ color: colors.text.secondary }}>
        No component data available.
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-1">
      {components.map(comp => (
        <div key={comp.component}>
          <button
            className="flex items-center gap-2 w-full text-left text-xs font-medium py-1.5 px-3 rounded hover:opacity-80"
            style={{
              backgroundColor: withOpacity(colors.text.secondary, 0.06),
            }}
            onClick={() => toggleComponent(comp.component)}
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{
                backgroundColor:
                  statusColors[comp.status] || colors.text.secondary,
              }}
            />
            <span>{comp.component}</span>
            <span
              className="ml-auto text-[10px]"
              style={{
                color: statusColors[comp.status] || colors.text.secondary,
              }}
            >
              {comp.status}
            </span>
          </button>

          {expandedComponents.has(comp.component) && comp.metrics && (
            <div className="ml-5 mt-0.5">
              <pre
                className="text-[10px] p-2 rounded overflow-auto max-h-40"
                style={{
                  color: colors.text.secondary,
                  backgroundColor: withOpacity(colors.text.secondary, 0.03),
                }}
              >
                {JSON.stringify(comp.metrics, null, 2)}
              </pre>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RunDetailContent({
  jobType,
  summary,
}: {
  jobType: string;
  summary: Record<string, unknown>;
}) {
  const colors = useThemeColors();

  if (jobType === JobType.WEEKLY_CARRY_OVER)
    return <CarryOverDetail summary={summary} />;
  if (jobType === JobType.SYNC_BOARD_TEMPLATES)
    return <TemplateSyncDetail summary={summary} />;
  if (jobType === JobType.SYSTEM_HEALTH_CHECK)
    return <HealthCheckDetail summary={summary} />;
  if (jobType === JobType.ENSURE_USER_BOARDS)
    return <EnsureUserBoardsDetail summary={summary} />;

  return (
    <pre
      className="text-xs p-4 overflow-auto max-h-96"
      style={{ color: colors.text.secondary }}
    >
      {JSON.stringify(summary, null, 2)}
    </pre>
  );
}

// ─── Run Summary (inline stats) ───────────────────────────────────────────────

function RunSummary({
  jobType,
  summary,
}: {
  jobType: string;
  summary: Record<string, unknown>;
}) {
  const colors = useThemeColors();

  if (jobType === JobType.WEEKLY_CARRY_OVER) {
    const processed = readOutput(summary, 'tasks_processed') ?? 0;
    const created = readOutput(summary, 'tasks_created') ?? 0;
    const skipped =
      readOutput(summary, 'skipped') === true || summary.skipped === true;

    const sourceWeek = readInput(summary, 'source_week');
    const sourceYear = readInput(summary, 'source_year');
    const targetWeek = readInput(summary, 'target_week');
    const targetYear = readInput(summary, 'target_year');

    return (
      <span>
        {skipped ? (
          <span style={{ color: colors.warning.text }}>
            Skipped (already ran this week)
          </span>
        ) : (
          <>
            {String(processed)} processed · {String(created)} created
            {sourceWeek != null && (
              <span style={{ color: colors.text.secondary }}>
                {' '}
                · W{String(sourceWeek)}/{String(sourceYear)} → W
                {String(targetWeek)}/{String(targetYear)}
              </span>
            )}
          </>
        )}
      </span>
    );
  }

  if (jobType === JobType.SYNC_BOARD_TEMPLATES) {
    const boardsCount = ((readOutput(summary, 'boards') ?? []) as unknown[])
      .length;
    return (
      <span>
        {boardsCount} board{boardsCount !== 1 ? 's' : ''} processed
      </span>
    );
  }

  if (jobType === JobType.SYSTEM_HEALTH_CHECK) {
    const components = (readOutput(summary, 'components') ??
      []) as ComponentSummary[];
    const healthy = components.filter(
      c => c.status === ComponentHealthStatus.HEALTHY
    ).length;
    const total = components.length;
    return (
      <span>
        {healthy}/{total} components healthy
      </span>
    );
  }

  if (jobType === JobType.ENSURE_USER_BOARDS) {
    const created = (readOutput(summary, 'boards_created') as number) ?? 0;
    const skipped = (readOutput(summary, 'boards_skipped') as number) ?? 0;
    const failed = (readOutput(summary, 'boards_failed') as number) ?? 0;
    const users = (readOutput(summary, 'users_checked') as number) ?? 0;
    return (
      <span>
        {created} board{created !== 1 ? 's' : ''} created
        {skipped > 0 && (
          <span style={{ color: colors.text.secondary }}>
            {' '}
            · {skipped} already existed
          </span>
        )}
        {failed > 0 && (
          <span style={{ color: colors.error }}> · {failed} failed</span>
        )}
        <span style={{ color: colors.text.secondary }}>
          {' '}
          · {users} user{users !== 1 ? 's' : ''} checked
        </span>
      </span>
    );
  }

  return null;
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5000;

interface JobRunDetailProps {
  runId: string;
}

export default function JobRunDetail({ runId }: JobRunDetailProps) {
  const { user } = useAuth();
  const colors = useThemeColors();

  const [run, setRun] = useState<GetJobRunsRunsItem | null>(null);
  const [jobType, setJobType] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchRun = async () => {
      try {
        const data = await executeQuery<GetJobRunInput, GetJobRunOutput>(
          Action.GET_JOB_RUN,
          { run_id: runId }
        );
        if (cancelled) return;
        setRun(data.run);
        setJobType(data.job.job_type);
        // Poll while running
        if (data.run.status === JobRunStatus.RUNNING) {
          pollRef.current = setTimeout(fetchRun, POLL_INTERVAL_MS);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        if (
          err instanceof QueryError &&
          err.code === ResponseCode.JOB_RUN_NOT_FOUND
        ) {
          setNotFound(true);
        }
        // On other errors (network, auth) leave in loading state — don't set notFound
      }
    };

    fetchRun();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [runId]);

  useScreenState({
    metadata: {
      screenType: ScreenType.JOB_RUN_DETAIL,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, state) => {
      state.setSuccess();
    },
  });

  if (!user) return null;

  if (notFound) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full gap-3"
        style={{ padding: spacing.lg, color: colors.text.secondary }}
      >
        <span className="text-sm">Run not found.</span>
        <span className="text-xs">
          The run may have been deleted or the link is incorrect.
        </span>
      </div>
    );
  }

  if (!run || !jobType) {
    return (
      <div
        className="flex items-center justify-center h-full"
        style={{ color: colors.text.secondary }}
      >
        <span className="text-sm">Loading run details...</span>
      </div>
    );
  }

  const jobLabel = JOB_TYPE_LABELS[jobType] || jobType;
  const summary = run.summary as Record<string, unknown> | undefined;

  const runStatusColors: Record<string, string> = {
    running: colors.primary,
    completed: colors.success,
    failed: colors.error,
    partial: colors.warning.text,
  };

  return (
    <div
      className="flex flex-col gap-6"
      style={{ padding: spacing.lg, color: colors.text.primary }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-base">{jobLabel}</span>
          <span className="text-xs" style={{ color: colors.text.secondary }}>
            Run Detail · {run.id}
          </span>
        </div>
        <ShareButton
          entity={DeepLinkEntity.JOB_RUN}
          id={run.id}
          ariaLabel="Copy run link"
        />
      </div>

      {/* Status card */}
      <div
        className="rounded-lg border p-4 flex flex-col gap-4"
        style={{
          backgroundColor: colors.offWhite,
          borderColor: colors.border.default,
        }}
      >
        {/* Status row */}
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[10px] uppercase tracking-wide"
              style={{ color: colors.text.secondary }}
            >
              Status
            </span>
            <span
              className="px-2.5 py-0.5 rounded-full text-xs font-semibold self-start"
              style={{
                backgroundColor: withOpacity(
                  runStatusColors[run.status] || colors.text.secondary,
                  0.12
                ),
                color: runStatusColors[run.status] || colors.text.secondary,
              }}
            >
              {RUN_STATUS_DISPLAY[run.status as JobRunStatus] ?? run.status}
            </span>
          </div>

          <div className="flex flex-col gap-0.5">
            <span
              className="text-[10px] uppercase tracking-wide"
              style={{ color: colors.text.secondary }}
            >
              Started
            </span>
            <span className="text-xs font-medium">
              {formatDateTime(run.started_at)}
            </span>
          </div>

          {run.completed_at && (
            <div className="flex flex-col gap-0.5">
              <span
                className="text-[10px] uppercase tracking-wide"
                style={{ color: colors.text.secondary }}
              >
                Completed
              </span>
              <span className="text-xs font-medium">
                {formatDateTime(run.completed_at)}
              </span>
            </div>
          )}

          <div className="flex flex-col gap-0.5">
            <span
              className="text-[10px] uppercase tracking-wide"
              style={{ color: colors.text.secondary }}
            >
              Duration
            </span>
            <span className="text-xs font-medium">
              {formatDuration(run.started_at, run.completed_at)}
            </span>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="text-xs" style={{ color: colors.text.secondary }}>
            <RunSummary jobType={jobType} summary={summary} />
          </div>
        )}

        {/* Error */}
        {run.error_details && (
          <div
            className="text-xs px-3 py-2 rounded"
            style={{
              backgroundColor: withOpacity(colors.error, 0.06),
              color: colors.error,
              border: `1px solid ${withOpacity(colors.error, 0.15)}`,
            }}
          >
            {run.error_details}
          </div>
        )}
      </div>

      {/* Detail content */}
      {summary ? (
        <div
          className="rounded-lg border overflow-hidden"
          style={{ borderColor: colors.border.default }}
        >
          <div
            className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide"
            style={{
              backgroundColor: colors.background,
              color: colors.text.secondary,
            }}
          >
            Details
          </div>
          <RunDetailContent jobType={jobType} summary={summary} />
        </div>
      ) : (
        <div
          className="text-xs text-center py-8"
          style={{ color: colors.text.secondary }}
        >
          No detail data available for this run.
        </div>
      )}
    </div>
  );
}
